import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const WS_RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 3;
const SSE_RECONNECT_DELAY = 2000;

export function useWebSocket(onMessage) {
  const { token } = useAuth();
  const wsRef = useRef(null);
  const sseRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionType, setConnectionType] = useState(null);

  // SSE connection (HTTP-based, more compatible with proxies)
  const connectSSE = useCallback((sseUrl) => {
    if (sseRef.current) return;

    try {
      console.log('Connecting to SSE:', sseUrl);
      sseRef.current = new EventSource(sseUrl);

      sseRef.current.onopen = () => {
        console.log('SSE connected');
        setIsConnected(true);
        setConnectionType('sse');
        reconnectAttemptsRef.current = 0;
      };

      sseRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connected') {
            console.log('Real-time sync active via SSE');
            return;
          }
          if (onMessage) onMessage(data);
        } catch (e) {
          console.error('Error parsing SSE message:', e);
        }
      };

      sseRef.current.onerror = (error) => {
        console.log('SSE error, will retry...');
        sseRef.current?.close();
        sseRef.current = null;
        setIsConnected(false);
        setConnectionType(null);

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connectSSE(sseUrl);
          }, SSE_RECONNECT_DELAY * reconnectAttemptsRef.current);
        }
      };

    } catch (error) {
      console.error('SSE not supported:', error);
      setIsConnected(false);
    }
  }, [onMessage]);

  // Try WebSocket first, fallback to SSE
  const connect = useCallback(() => {
    if (!token) return;
    
    if (wsRef.current?.readyState === WebSocket.OPEN || sseRef.current) {
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss:' : 'ws:';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/${token}`;
    const sseUrl = `${backendUrl}/api/sse/${token}`;

    try {
      console.log('Trying WebSocket connection...');
      wsRef.current = new WebSocket(wsUrl);

      const wsTimeout = setTimeout(() => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          console.log('WebSocket timeout, falling back to SSE');
          wsRef.current?.close();
          wsRef.current = null;
          connectSSE(sseUrl);
        }
      }, 3000);

      wsRef.current.onopen = () => {
        clearTimeout(wsTimeout);
        console.log('WebSocket connected');
        setIsConnected(true);
        setConnectionType('websocket');
        reconnectAttemptsRef.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          if (event.data === 'ping') {
            wsRef.current?.send('pong');
            return;
          }
          if (event.data === 'pong') return;

          const data = JSON.parse(event.data);
          if (data.type === 'connected') {
            console.log('Real-time sync active via WebSocket');
            return;
          }
          if (onMessage) onMessage(data);
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        clearTimeout(wsTimeout);
        console.log('WebSocket closed:', event.code);
        wsRef.current = null;
        
        if (isConnected && connectionType === 'websocket') {
          setIsConnected(false);
          setConnectionType(null);
        }
        
        // If not normal close, try SSE
        if (event.code !== 1000 && !sseRef.current) {
          connectSSE(sseUrl);
        }
      };

      wsRef.current.onerror = () => {
        clearTimeout(wsTimeout);
        console.log('WebSocket error, falling back to SSE');
        wsRef.current?.close();
        wsRef.current = null;
        connectSSE(sseUrl);
      };

    } catch (error) {
      console.log('WebSocket not supported, using SSE');
      connectSSE(sseUrl);
    }
  }, [token, onMessage, connectSSE, isConnected, connectionType]);

  // Connect on mount / token change
  useEffect(() => {
    if (token) {
      connect();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [token, connect]);

  const reconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectAttemptsRef.current = 0;
    setIsConnected(false);
    setConnectionType(null);
    
    setTimeout(() => connect(), 100);
  }, [connect]);

  return { isConnected, reconnect, connectionType };
}
